import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSerialPort } from "../context/SerialPortContext";
import { CreditCard } from "lucide-react";
import { findUser } from "../api/userApi";

export default function SerialPortComponent({ onCardData }) {
  const {
    isSerialSupported,
    isConnected,
    requestPort,
    connect,
    disconnect,
    autoConnect,
    addEventListener,
    eventTypes,
  } = useSerialPort();

  const [autoConnectAttempted, setAutoConnectAttempted] = useState(false);
  const [user, setUser] = useState(null);
  const [receivedData, setReceivedData] = useState("");
  const receivedDataRef = useRef("");

  const handlePortConnection = useCallback(async () => {
    if (isConnected) {
      // If already connected, disconnect
      await disconnect();
    } else {
      // If not connected, request port and connect
      const port = await requestPort();
      if (port) await connect(port);
    }
  }, [isConnected, requestPort, connect, disconnect]);
  // Try to auto-connect on component mount
  useEffect(() => {
    const attemptAutoConnect = async () => {
      if (!isConnected && !autoConnectAttempted) {
        setAutoConnectAttempted(true);
        const success = await autoConnect();
        console.log("Auto-connect attempt:", success ? "successful" : "failed");
      }
    };

    attemptAutoConnect();
  }, [autoConnect, isConnected, autoConnectAttempted]);
  // Parse received messages from the card reader
  const parseMessage = useCallback((message) => {
    const normalizedMessage = message.trim();
    const messagePattern =
      /^%([\dA-Za-z]+)\?;([\dA-Za-z]+)\?\+([\dA-Za-z]+)\?(\d)$/;
    const match = messagePattern.exec(normalizedMessage);

    if (match) {
      return {
        isValid: true,
        part1: match[1],
        part2: match[2],
        part3: match[3],
        part4: match[4],
      };
    } else {
      return { isValid: false };
    }
  }, []);

  useEffect(() => {
    if (!receivedData) return;

    const processUser = async () => {
      const result = parseMessage(receivedData);

      if (result.isValid) {
        console.log("Valid card data detected:", result);
        onCardData(result);
        return;
      }

      console.log("Invalid or incomplete data, waiting for more...");
    };

    processUser();
  }, [receivedData, onCardData, parseMessage]);
  const messageTimeoutRef = useRef(null);
  // Event listeners for serial port events
  useEffect(() => {
    // Register event listeners

    const removeDataListener = addEventListener(eventTypes.DATA, (data) => {
      const decoder = new TextDecoder();
      const decoded = decoder.decode(data);

      const nextData = receivedDataRef.current + decoded;
      receivedDataRef.current = nextData;
      setReceivedData(nextData);

      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }

      messageTimeoutRef.current = setTimeout(() => {
        console.log("No more data received for 200ms. Finalizing message.");
        const finalMessage = receivedDataRef.current;
        const result = parseMessage(finalMessage);

        if (result.isValid) {
          console.log("Valid card data detected on timeout:", result);
          onCardData(result);
        } else if (finalMessage) {
          console.log("Invalid or incomplete data on timeout:", finalMessage);
        }

        receivedDataRef.current = "";
        setReceivedData("");
        messageTimeoutRef.current = null;
      }, 1000);
    });

    const removeConnectedListener = addEventListener(
      eventTypes.CONNECTED,
      (port) => {
        console.log("Connected to port:", port);
        // Reset user when connecting
        setUser(null);
      }
    );

    const removeDisconnectedListener = addEventListener(
      eventTypes.DISCONNECTED,
      (port) => {
        console.log("Disconnected from port:", port);
        // Reset user when disconnecting
        setUser(null);
      }
    );

    // Clean up event listeners on unmount
    return () => {
      removeDataListener();
      removeConnectedListener();
      removeDisconnectedListener();
    };
  }, [addEventListener, eventTypes]);

  // Handle error with proper fallback UI
  if (!isSerialSupported) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-md">
        Web Serial API is not supported in this browser. Please try Chrome,
        Edge, or Opera.
      </div>
    );
  }

  // Determine button color based on connection status
  const buttonColorClass = isConnected
    ? "bg-green-500 hover:bg-green-600" // Connected - green
    : "bg-gray-300 hover:bg-gray-400"; // Not connected - gray

  return (
    <div className="flex flex-col gap-3">
      <button
        className={`px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-amber-300 flex items-center gap-2  transition-colors ${buttonColorClass}`}
        onClick={handlePortConnection}
        title={
          isConnected
            ? "Connected (click to disconnect)"
            : "Not connected (click to connect)"
        }
      >
        <svg
          className=" w-4 h-4"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.5 0A1.5 1.5 0 0116 1.5V9h-3V0h1.5zM11 0H5.5A1.5 1.5 0 004 1.5V9h7V0zM3.5 11A1.5 1.5 0 002 12.5V18H1a1 1 0 100 2h18a1 1 0 100-2h-1v-5.5a1.5 1.5 0 00-1.5-1.5h-13z"
            fill={isConnected ? "#eff6ff" : "#ff0000"}
          ></path>
        </svg>
      </button>

      {/* User Information Display */}
      {user && (
        <div className="mt-2 p-4 border rounded-md bg-blue-50">
          <h3 className="font-semibold text-lg mb-2">User Detected</h3>
          <div className="flex flex-col gap-1">
            <p>
              <span className="font-medium">Name:</span> {user.full_name}
            </p>
            <p>
              <span className="font-medium">User Name:</span> {user.user_name}
            </p>
            <p>
              <span className="font-medium">Email:</span> {user.user_email}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
